import type { CSSObject } from '@emotion/react'
import {
    luminance,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
} from '@storefronts/common-types'

/**
 * Controls are painted from merchant-set custom properties, so `color-mix` is
 * what lets a hover colour be derived from whatever they resolve to at paint
 * time. 22% reads as the control responding while keeping a filled button's
 * label legible across the range of brand colours merchants pick.
 */
const DARKEN_PCT = 22

/** A transparent control has nothing to darken, so it picks up a faint wash. */
const TINT_PCT = 8

const TRANSITION: CSSObject = {
    transition:
        'background-color 120ms ease, color 120ms ease, border-color 120ms ease, opacity 120ms ease',
}

/**
 * Pointer devices only: a tap on a touch screen leaves the hover state stuck
 * on the last thing tapped. `:disabled` and `[aria-disabled]` cover both the
 * native buttons and the divs the older components still use.
 */
export const onHover = (styles: CSSObject): CSSObject => ({
    ...TRANSITION,
    '@media (hover: hover)': {
        '&:hover:not(:disabled):not([aria-disabled="true"])': styles,
    },
})

export const darken = (color: string): string =>
    `color-mix(in srgb, ${color} ${100 - DARKEN_PCT}%, #000)`

export const hoverDarkenFill = (fillColor: string): CSSObject =>
    onHover({ backgroundColor: darken(fillColor) })

/**
 * A hollow control has no fill to darken, so the colour it is drawn in becomes
 * the fill and the label flips to the surface behind it.
 */
export const hoverInvertOutline = (
    outlineColor: string,
    labelColor: string,
): CSSObject =>
    onHover({
        backgroundColor: outlineColor,
        color: labelColor,
        borderColor: outlineColor,
    })

/**
 * White and the off-whites merchants use for a hollow button have nowhere to
 * go: a darker shade of them reads as the control greying out rather than
 * responding.
 */
const FLIP_ABOVE_LUMINANCE = 0.75

/**
 * A light fill swaps places with its label, anything darker takes the darker
 * shade of itself. A colour only resolvable at paint time can't be measured,
 * so it darkens, which is the right answer for the brand fills those hold.
 */
export const hoverFill = (fillColor: string, labelColor: string): CSSObject => {
    const fillLuminance = luminance(fillColor)
    return fillLuminance !== null && fillLuminance > FLIP_ABOVE_LUMINANCE
        ? hoverInvertOutline(labelColor, fillColor)
        : hoverDarkenFill(fillColor)
}

/**
 * For a control that is only text, where a fill would fight the surrounding
 * copy: accordion headings, text links, disclosure rows.
 */
export const hoverFadeText = (): CSSObject => onHover({ opacity: 0.7 })

/** For controls with no fill and no outline: icon buttons, steppers, rows. */
export const hoverTintSurface = (
    tintColor = `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
): CSSObject =>
    onHover({
        backgroundColor: `color-mix(in srgb, ${tintColor} ${TINT_PCT}%, transparent)`,
    })
